const FILTERING_FACET_PREFIX = '__filtering__';

/**
 * @param {string} key
 * @returns {string}
 */
export function filteringFacetKey(key) {
  return `${FILTERING_FACET_PREFIX}${key}`;
}

/**
 * Normalizes one value from the custom.filtering JSON metafield.
 * Semicolon-delimited strings are treated as multiple filter choices.
 *
 * @param {unknown} raw
 * @returns {string | string[] | null}
 */
export function normalizeFilteringValue(raw) {
  if (raw == null) return null;

  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';

  if (Array.isArray(raw)) {
    const values = raw
      .flatMap((item) => {
        const normalized = normalizeFilteringValue(item);
        return Array.isArray(normalized) ? normalized : [normalized];
      })
      .filter((item) => typeof item === 'string' && item.length > 0);

    return values.length ? [...new Set(values)] : null;
  }

  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? String(raw) : null;
  }

  if (typeof raw !== 'string') return null;

  const values = raw
    .split(';')
    .map((value) => value.trim().replace(/\s+/g, ' '))
    .filter(Boolean);

  if (values.length === 0) return null;
  return values.length === 1 ? values[0] : [...new Set(values)];
}

/**
 * Builds namespaced facet values from the custom.filtering JSON metafield.
 * Namespacing keeps the curated filter data separate from legacy specs that
 * happen to use the same display labels.
 *
 * @param {Record<string, unknown> | null | undefined} filtering
 * @returns {Record<string, string | string[]>}
 */
export function buildFilteringFacetValueMap(filtering) {
  const out = {};
  if (!filtering || typeof filtering !== 'object' || Array.isArray(filtering)) return out;

  for (const [rawKey, rawValue] of Object.entries(filtering)) {
    const key = rawKey.trim();
    if (!key) continue;

    const value = normalizeFilteringValue(rawValue);
    if (value == null) continue;

    out[filteringFacetKey(key)] = value;
  }

  return out;
}
